/* Calls to the FastAPI backend. Every request goes through here. */

import type { Alternatives, Analysis, AuthConfig, Comparison, ContributeDraft, ContributeResult, Facets, Health, ProductLookup, SearchQuery, SearchResult, User } from "./types";

/** An error the backend reported, with its message ready to show. */
export class ApiError extends Error {}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON: an error page from nginx in front of the app, not the app itself.
    const reason =
      response.status === 413
        ? "the photos are too large to upload; retake them a little further away"
        : response.status === 502 || response.status === 503 || response.status === 504
          ? "the server is busy or restarting; try again in a minute"
          : "the server sent an unreadable response";
    throw new ApiError(`${fallback} (${reason}).`);
  }
  if (!response.ok) {
    const detail = (body as { detail?: unknown } | null)?.detail;
    throw new ApiError(typeof detail === "string" ? detail : fallback);
  }
  return body as T;
}

/** OCR both labels (and optional front images) and compare them. */
export async function extract(form: FormData): Promise<Comparison> {
  const response = await fetch("/api/extract", { method: "POST", body: form });
  return readJson<Comparison>(response, "Reading the labels failed.");
}

/** Analyse one product, from its barcode or its ingredients photo. */
export async function analyse(form: FormData): Promise<Analysis> {
  const response = await fetch("/api/analyse", { method: "POST", body: form });
  return readJson<Analysis>(response, "Analysing the product failed.");
}

/** Look a barcode up in Open Food Facts, through our backend. */
export async function lookupProduct(barcode: string): Promise<ProductLookup> {
  const response = await fetch("/api/product/" + encodeURIComponent(barcode));
  return readJson<ProductLookup>(response, "Barcode lookup failed.");
}

/** Better-processed products in the same category as this barcode. */
export async function lookupAlternatives(barcode: string): Promise<Alternatives> {
  const response = await fetch("/api/product/" + encodeURIComponent(barcode) + "/alternatives");
  return readJson<Alternatives>(response, "Looking for alternatives failed.");
}

export async function health(): Promise<Health> {
  const response = await fetch("/api/health");
  return readJson<Health>(response, "Health check failed.");
}

/** Turn any failure into a sentence a user can act on. A failed fetch while
 *  offline should say so, not surface "Failed to fetch". */
export function describeFailure(error: unknown): string {
  if (!navigator.onLine) {
    return "You're offline. Reading labels needs a connection to the server.";
  }
  if (error instanceof ApiError) return error.message;
  if (error instanceof TypeError) return "Could not reach the server. Is it still running?";
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** Search (with a text query) or browse (without) the catalogue. */
export async function searchProducts(query: SearchQuery, signal?: AbortSignal): Promise<SearchResult> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== "" && value !== undefined) params.set(key, String(value));
  const response = await fetch("/api/search?" + params.toString(), { signal });
  return readJson<SearchResult>(response, "Search failed.");
}

/** Common brands and categories, for the filters. */
export async function fetchFacets(): Promise<Facets> {
  const response = await fetch("/api/facets");
  return readJson<Facets>(response, "Loading filters failed.");
}

/** Product names starting like `q`, for the search box. */
export async function suggestNames(q: string, signal?: AbortSignal): Promise<string[]> {
  const response = await fetch("/api/suggest?q=" + encodeURIComponent(q), { signal });
  const body = await readJson<{ suggestions: { name: string }[] }>(response, "Suggestions failed.");
  return [...new Set(body.suggestions.map((s) => s.name))];
}

/** What "Add to Open Food Facts" would send for a product read from photos. */
export async function contributeDraft(barcode: string): Promise<ContributeDraft> {
  const response = await fetch("/api/product/" + encodeURIComponent(barcode) + "/contribute");
  return readJson<ContributeDraft>(response, "Couldn't prepare the Open Food Facts entry.");
}

/** Send a checked product, and the chosen photos, to Open Food Facts. */
export async function contributeProduct(barcode: string, fields: Record<string, unknown>): Promise<ContributeResult> {
  const response = await fetch("/api/product/" + encodeURIComponent(barcode) + "/contribute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  return readJson<ContributeResult>(response, "Adding the product to Open Food Facts failed.");
}

/** Whether Google sign-in is on, and its client ID. */
export async function authConfig(): Promise<AuthConfig> {
  const response = await fetch("/api/auth/config");
  return readJson<AuthConfig>(response, "Couldn't check sign-in.");
}

/** The signed-in user, or null. */
export async function currentUser(): Promise<User | null> {
  const response = await fetch("/api/auth/me");
  return (await readJson<{ user: User | null }>(response, "Couldn't check sign-in.")).user;
}

/** Sign in with the ID token Google gave the page; the server sets the session cookie. */
export async function signInWithGoogle(credential: string): Promise<User> {
  const response = await fetch("/api/auth/google", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  });
  return (await readJson<{ user: User }>(response, "Signing in failed.")).user;
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}
